/* eslint-disable @typescript-eslint/no-explicit-any */
import { TLabel, useSubscription } from '@apia/util';
import { CollectorField } from './CollectorField';
import { TValidationRules, TValidationFunction } from '../types';
import { validationsStore } from '../validationStore';
import { Collector } from './Collector';

export abstract class ValidatedCollectorField<
  ValueType = any,
  FieldProps = any,
> extends CollectorField<ValueType, FieldProps> {
  constructor(
    public readonly name: string,
    protected label: string | TLabel,
    protected config: Partial<{
      initialValue: ValueType;
      properties: Partial<FieldProps>;
      validationFunction: TValidationFunction<ValueType>;
      validationRules: TValidationRules;
    }> = {},
  ) {
    super(name, label, config?.properties);
  }

  set collector(c: Collector) {
    this._collector = c;

    validationsStore.registerField(this._collector.id, {
      name: this.name,
      validationFunction: this.config.validationFunction,
      validationRules: this.config.validationRules,
      value: this.config.initialValue,
    });
  }

  get initialValue() {
    return this.config.initialValue;
  }

  get value() {
    return validationsStore.getFieldStore(this._collector.id, this.name)
      .value as ValueType;
  }

  setInitialValue = (newValue: ValueType) => {
    this.config.initialValue = newValue;
  };

  setValidationFunction = (newFunction: TValidationFunction<ValueType>) => {
    this.config.validationFunction = newFunction;
    this.emit('functionUpdate', this.config.validationFunction);
  };

  setValidationRules = (newRules: TValidationRules) => {
    this.config.validationRules = newRules;
    this.emit('rulesUpdate', { ...this.config.validationRules });
  };

  setValue(newValue: ValueType) {
    validationsStore.setFieldValue(this._collector.id, this.name, newValue);
  }

  toggleRequired = (
    isReq = this.config?.validationRules?.required ?? false,
  ) => {
    if (!this.config.validationRules) {
      this.config.validationRules = {};
    }
    this.config.validationRules.required = isReq;
  };

  protected useValidationFunction = () => {
    return useSubscription<TValidationFunction<ValueType> | undefined>({
      getInitialState: () => () => this.config.validationFunction,
      makeSubscription: (setValidationFunction) => {
        return this.on('functionUpdate', setValidationFunction);
      },
    });
  };

  protected useValidationRules = () => {
    return useSubscription({
      getInitialState: () => this.config.validationRules,
      makeSubscription: (setValidationRules) => {
        return this.on('rulesUpdate', setValidationRules);
      },
    });
  };
}
